import React from 'react';
import { Modal, ModalProps } from '@dropbox/dig-components/modal';
import { Box, Split } from '@dropbox/dig-foundations';
import { Button, IconButton } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { DownloadLine, PrintLine } from '@dropbox/dig-icons/assets';
import { unescape } from 'lodash';
import { EsignDisclosure } from 'signer-app/context/signer-app-client/signer-api';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@dropbox/dig-components/typography';
import { getBrandName } from 'signer-app/utils/brand';
import { redirectTo } from 'signer-app/utils/redirect';
import { printPage } from 'signer-app/utils/print';
import styles from 'signer-app/esign-disclosure/esign-disclosure-modal.module.css';

const messages = defineMessages({
  downloadDisclosure: {
    id: '6b6338b5553e211e1eccbaf518a3961607261f239ae83befc4fa152fd13be69a',
    description: 'Download the disclosure, esign disclosure modal',
    defaultMessage: 'Download',
  },
  printDisclosure: {
    id: '7180b8cef9467ffaa5aa6d9e4a9437931e9b16a87764ab4ec36603604f543608',
    description: 'Print the disclosure, esign disclosure modal',
    defaultMessage: 'Print',
  },
  header: {
    id: '97205237e137976f2068d3170956b4a0acfd6258fad92215aed8ed4ab63ae57f',
    description: 'modal dialog header text, esign disclosure',
    defaultMessage:
      'Agreement to do business electronically with {businessName}',
  },
  body1: {
    id: '5a7c53a3a16f45978cc238165327d3fdbbed2b72becec442b8c343d18caff1af',
    description: 'modal dialog body text, first sentence, esign disclosure',
    defaultMessage:
      "Described below are the terms and conditions for providing to you documents and disclosures electronically through the {brandName} electronic signing system. If you consent to receiving notices and disclosures in electronic format based on the terms and conditions described below, please let us know by clicking the 'I agree' button.",
  },
  body2: {
    id: 'a6dd19a5a6f687ce807410d3341437026b09d7e272d574352e3b2e48f6b9e9ba',
    description: 'modal dialog body text, second sentence, esign disclosure',
    defaultMessage: "By checking the 'I agree' box, I confirm that:",
  },
  listItem1: {
    id: 'facb36317d04184493d33039db78016c92a5e8092cb28c789a1a582dea51ce7a',
    description: 'modal dialog body text, list item 1, esign disclosure',
    defaultMessage:
      'I can access and read this Electronic CONSENT TO ELECTRONIC RECEIPT OF ELECTRONIC CONSUMER DISCLOSURES document; and',
  },
  listItem2: {
    id: '67714bfe5f6063602dfcaf47cf63ec5b3a2cc5ff8627dfadf121aa4356d8696f',
    description: 'modal dialog body text, list item 2, esign disclosure',
    defaultMessage:
      'I can print on paper the disclosure or save or send the disclosure to a place where I can print it, for future reference and access; and',
  },
  listItem3: {
    id: 'eae5c9f534765eeac91336c08ef2ae53ad85788b3783968c80a88641e6d07096',
    description: 'modal dialog body text, list item 3, esign disclosure',
    defaultMessage:
      'Until or unless I notify {businessName}, I consent to receive from exclusively through electronic means all notices, disclosures, authorizations, acknowledgements, and other documents that are required to be provided or made available to me by {businessName} during the course of my relationship with you.',
  },
  additionalText: {
    id: '31abfacba84471359678419b8ab20c453db86bc199f491919c081ee35c59e9de',
    description: 'modal dialog body text, additional text, esign disclosure',
    defaultMessage:
      'In addition to the above, the sender has added the following custom information:',
  },
  gotIt: {
    id: 'd5c0eb79d19ad184e1a03603307f84e99bcaadaf90ee51d6d423122cc5a1290d',
    description: 'Confirm button text, esign disclosure',
    defaultMessage: 'Got it',
  },
  close: {
    id: '364f80a5bd4f5027426bbf7ad09cfe32dd57ba26e00cc18d98746dd9dd579976',
    description: 'Close button text, esign disclosure',
    defaultMessage: 'Close',
  },
});

type Props = {
  open: boolean;
  esignDisclosure: EsignDisclosure;
  downloadUrl: string;
  onRequestClose?: ModalProps['onRequestClose'];
};

export function EsignDisclosureModal({
  open,
  esignDisclosure,
  downloadUrl,
  onRequestClose,
}: Props) {
  const intl = useIntl();

  function onDownload() {
    redirectTo(downloadUrl);
  }

  function onPrint() {
    printPage();
  }

  const businessName = unescape(esignDisclosure.businessName);

  return (
    <Modal
      open={open}
      className={styles.modal}
      onRequestClose={onRequestClose}
      withCloseButton={intl.formatMessage(messages.close)}
      closeButtonProps={{ className: styles.closeButton }}
      aria-labelledby="disclosure-title"
    >
      <Modal.Header>
        <Modal.Title id="disclosure-title">
          {intl.formatMessage(messages.header, {
            businessName,
          })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Box paddingTop="Micro Medium" paddingBottom="Micro XLarge">
          <Text variant="paragraph" tagName="p">
            {intl.formatMessage(messages.body1, {
              brandName: getBrandName('S'),
            })}
          </Text>
          <Text variant="paragraph" tagName="p">
            {intl.formatMessage(messages.body2)}
          </Text>
          <Box>
            <ul>
              <li>
                <Text>{intl.formatMessage(messages.listItem1)}</Text>
              </li>
              <li>
                <Text>{intl.formatMessage(messages.listItem2)}</Text>
              </li>
              <li>
                <Text>
                  {intl.formatMessage(messages.listItem3, {
                    businessName,
                  })}
                </Text>
              </li>
            </ul>
          </Box>
          {esignDisclosure.additionalText && (
            <>
              <Text variant="paragraph" tagName="p" isBold>
                {intl.formatMessage(messages.additionalText)}
              </Text>
              <Text variant="paragraph" tagName="p">
                {unescape(esignDisclosure.additionalText)}
              </Text>
            </>
          )}
        </Box>
      </Modal.Body>
      <Modal.Footer
        preferComposition
        hasTopBorder
        className={styles.modalFooter}
      >
        <Split gap="Micro Medium">
          <Split.Item marginRight="auto">
            <Split gap="Micro XSmall">
              <Split.Item>
                <IconButton variant="borderless" onClick={onDownload}>
                  <UIIcon
                    src={DownloadLine}
                    aria-label={intl.formatMessage(messages.downloadDisclosure)}
                  />
                </IconButton>
              </Split.Item>
              <Split.Item>
                <IconButton variant="borderless" onClick={onPrint}>
                  <UIIcon
                    src={PrintLine}
                    aria-label={intl.formatMessage(messages.printDisclosure)}
                  />
                </IconButton>
              </Split.Item>
            </Split>
          </Split.Item>
          <Split.Item>
            <Button variant="primary" onClick={onRequestClose}>
              {intl.formatMessage(messages.gotIt)}
            </Button>
          </Split.Item>
        </Split>
      </Modal.Footer>
    </Modal>
  );
}
